import { queryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
	type HailProtection as AppSyncHailProtection,
	type Protection as AppSyncProtection,
	type WeatherHazardProtection as AppSyncWeatherHazardProtection,
	type WindProtection as AppSyncWindProtection,
	type CategoricalRule,
	ChildRuleSchema,
	type CreateProtection,
	type DeleteProtection,
	type ForecastRule,
	HailProtectionSchema,
	ProtectionSchema,
	RuleSourcesSchema,
	type UpdateProtection,
	WeatherHazardProtectionSchema,
	WindProtectionSchema,
} from '@xweather/protect-types'
import { isNotNil } from 'es-toolkit'
import { z } from 'zod'
import { createGraphQLClient } from '../../api/helpers.ts'
import { env } from '../../env.ts'
import { parseCapSeverity } from '../alerts/utils.ts'
import { type Impersonation, useImpersonation } from '../impersonation.ts'
import { createProtection, deleteProtection, getProtection, getProtections, updateProtection } from './graphql.ts'
import {
	type AnyFullProtection,
	type AnyProtection,
	type ForecastHailProtection,
	type ForecastPrecipitationProtection,
	type ForecastWindProtection,
	type FullWeatherHazardProtection,
	ProtectionPriority,
	type RequiredUserLists,
} from './types.ts'

const getClient = (impersonation: Impersonation | undefined) => createGraphQLClient(env.API_PROTECTIONS, impersonation)

/**
 * The protect team is planning on moving to API Gateway and using an OpenAPI spec.
 * Therefore,the typing will be mostly ignored in this file for now. Some type is also
 * reliant on use exporting our backend type package that we use in our monorepo.
 */
const fetchAllProtections = async (impersonation: Impersonation | undefined): Promise<IncomingProtection[]> => {
	const allItems: IncomingProtection[] = []
	let nextToken: string | null = null

	do {
		const variables = { token: nextToken }
		const response: { getProtections: { items: IncomingProtection[]; nextToken: string | null } } = await getClient(impersonation).request(
			getProtections,
			variables,
		)
		const data = response.getProtections
		for (const item of data.items) {
			const parsed = IncomingProtectionSchema.safeParse(item)
			if (parsed.success) {
				allItems.push(parsed.data)
			} else {
				console.error('Protection parsing error:', parsed.error)
			}
		}
		nextToken = data.nextToken
	} while (nextToken)

	return allItems
}

const incomingProtectionsQuery = (impersonation: Impersonation | undefined, options: { enabled: boolean }) =>
	queryOptions({
		queryKey: ['PROTECTIONS', impersonation?.subscriptionId],
		refetchInterval: 1000 * 60,
		staleTime: 1000 * 60,
		enabled: options.enabled,
		queryFn: async (): Promise<IncomingProtection[]> => {
			try {
				return await fetchAllProtections(impersonation)
			} catch (error) {
				console.error('Error fetching protections:', error)
				return []
			}
		},
	})

export const toProtections = (items: IncomingProtection[]): AnyProtection[] => items.map(toProtection).filter(isNotNil)

const toLtzProtectionAssets = (items: IncomingProtection[]): Record<string, string> =>
	Object.fromEntries(items.filter(isLtzProtection).map((protection) => [protection.vrn, protection.context.assetVrn]))

export const protectionsQuery = (impersonation: Impersonation | undefined, options: { enabled: boolean }) =>
	queryOptions({ ...incomingProtectionsQuery(impersonation, options), select: toProtections })

export const ltzProtectionAssetsQuery = (impersonation: Impersonation | undefined, options: { enabled: boolean }) =>
	queryOptions({ ...incomingProtectionsQuery(impersonation, options), select: toLtzProtectionAssets })

export const protectionQuery = (vrn: string, impersonation: Impersonation | undefined) => ({
	queryKey: ['PROTECTION', vrn, impersonation?.subscriptionId],
	refetchInterval: 1000 * 60,
	staleTime: 1000 * 60,
	queryFn: async (): Promise<AnyFullProtection | null> => {
		const response: { getProtection: { item: IncomingProtectionFull } } = await getClient(impersonation).request(getProtection, { vrn })
		const item = response.getProtection.item
		const parsedProtection = IncomingProtectionFullSchema.safeParse(item)
		if (!parsedProtection.success) {
			console.error('Full protection parsing error:', parsedProtection.error)
			return null
		}
		return toFullProtection(parsedProtection.data)
	},
})

export const useProtection = ({ vrn }: { vrn: string }) => useQuery(protectionQuery(vrn, useImpersonation()))

export const useCreateProtection = () => {
	const impersonation = useImpersonation()
	const queryClient = useQueryClient()
	return useMutation({
		mutationFn: (input: CreateProtection) => {
			const payload = {
				...input,
				parentRule: {
					...input.parentRule,
					sources: JSON.stringify(input.parentRule.sources),
					rules: input.parentRule.rules.map((rule) => JSON.stringify(rule)),
				},
			}
			return getClient(impersonation).request(createProtection, { input: payload })
		},
		onSuccess: () => queryClient.invalidateQueries({ queryKey: ['PROTECTIONS', impersonation?.subscriptionId] }),
		onError: (error) => console.error('Error creating protection:', error),
	})
}

export const useDeleteProtection = () => {
	const impersonation = useImpersonation()
	const queryClient = useQueryClient()
	return useMutation({
		mutationFn: (input: DeleteProtection) => getClient(impersonation).request(deleteProtection, { input }),
		onSuccess: () => queryClient.invalidateQueries({ queryKey: ['PROTECTIONS', impersonation?.subscriptionId] }),
		onError: (error) => console.error('Error deleting protection:', error),
	})
}

export const useUpdateProtection = () => {
	const impersonation = useImpersonation()
	const queryClient = useQueryClient()
	return useMutation({
		mutationFn: (input: UpdateProtection) => {
			const payload = {
				...input,
				parentRule: {
					...input.parentRule,
					sources: JSON.stringify(input.parentRule.sources),
					rules: input.parentRule.rules.map((rule) => JSON.stringify(rule)),
				},
			}
			return getClient(impersonation).request(updateProtection, { input: payload })
		},
		onSuccess: () => queryClient.invalidateQueries({ queryKey: ['PROTECTIONS', impersonation?.subscriptionId] }),
		onError: (error) => console.error('Error updating protection:', error),
	})
}

const parseImmediateUserList = (notification: AppSyncProtection['notification']) => {
	const userLists: RequiredUserLists<'immediate'> = { immediate: [] }
	if ('destinationListVrns' in notification && notification.destinationListVrns) {
		userLists.immediate = notification.destinationListVrns.map((vrn) => ({
			vrn: vrn,
		}))
	}
	return userLists
}

const parseScheduledUserList = (notification: AppSyncProtection['notification']) => {
	const userLists: RequiredUserLists<'scheduled'> = { scheduled: [] }
	if ('schedules' in notification && notification.schedules) {
		userLists.scheduled = notification.schedules.map((schedule) => ({
			vrn: schedule.destinationListVrn,
			times: schedule.times,
			timezone: schedule.timezone,
		}))
	}
	return userLists
}

const toForecastHailProtection = (hail: AppSyncHailProtection): ForecastHailProtection | null => {
	const parentRule = hail.parentRule
	if (!parentRule || !Array.isArray(parentRule.rules) || !parentRule.rules[0]) return null

	const rule = parentRule.rules[0]
	const HAIL_SEVERE_PROBABILITY = rule.rules?.find((r) => r.parameter === 'HAIL_SEVERE_PROBABILITY')?.trigger?.value

	if (typeof HAIL_SEVERE_PROBABILITY !== 'number') return null

	return {
		type: 'hail',
		parameterType: 'forecast',
		priority: ProtectionPriority.Hail,
		vrn: hail.vrn,
		assetVrn: hail.context.assetVrn,
		createdTime: hail.createdTime,
		horizonMinutes: rule.horizonMinutes,
		HAIL_SEVERE_PROBABILITY,
		bufferKm: hail.context.bufferMeters / 1000,
		activeTimeoutMinutes: hail.activeTimeoutMinutes ?? 0,
		userLists: parseImmediateUserList(hail.notification),
	}
}

const toForecastPrecipitationProtection = (protection: IncomingProtectionFull): ForecastPrecipitationProtection | null => {
	const forecastRule = protection.parentRule.rules.find((r): r is ForecastRule => r.kind === 'FORECAST')
	if (!forecastRule) return null

	// Protections saved before the liquid accumulations set their thresholds on the total ones.
	const findBreachValue = (window: '1HR' | '6HR' | '24HR' | '72HR') =>
		forecastRule.rules.find(
			(r) => r.parameter === `PRECIPITATION_LIQUID_ACCUM_${window}_MM` || r.parameter === `PRECIPITATION_ACCUM_${window}_MM`,
		)?.trigger.value

	return {
		type: 'precipitation',
		parameterType: 'forecast',
		priority: ProtectionPriority.Precipitation,
		vrn: protection.vrn,
		assetVrn: protection.context.assetVrn,
		createdTime: protection.createdTime,
		horizonMinutes: forecastRule.horizonMinutes,
		PRECIPITATION_LIQUID_ACCUM_1HR_MM: findBreachValue('1HR'),
		PRECIPITATION_LIQUID_ACCUM_6HR_MM: findBreachValue('6HR'),
		PRECIPITATION_LIQUID_ACCUM_24HR_MM: findBreachValue('24HR'),
		PRECIPITATION_LIQUID_ACCUM_72HR_MM: findBreachValue('72HR'),
		userLists: parseScheduledUserList(protection.notification),
	}
}

const toProtection = (protection: IncomingProtection): AnyProtection | null => {
	if (isHailProtection(protection)) {
		return {
			type: 'hail',
			parameterType: 'forecast',
			priority: ProtectionPriority.Hail,
			vrn: protection.vrn,
			assetVrn: protection.context.assetVrn,
			createdTime: protection.createdTime,
		}
	}
	if (isWindProtection(protection)) {
		return {
			type: 'wind',
			parameterType: 'forecast',
			priority: ProtectionPriority.Wind,
			vrn: protection.vrn,
			assetVrn: protection.context.assetVrn,
			createdTime: protection.createdTime,
		}
	}
	if (isWeatherHazardProtection(protection)) {
		return {
			type: 'weatherHazard',
			parameterType: 'forecast',
			priority: ProtectionPriority.WeatherHazard,
			vrn: protection.vrn,
			assetVrn: protection.context.assetVrn,
			createdTime: protection.createdTime,
		}
	}
	if (isPrecipitationProtection(protection)) {
		return {
			type: 'precipitation',
			parameterType: 'forecast',
			priority: ProtectionPriority.Precipitation,
			vrn: protection.vrn,
			assetVrn: protection.context.assetVrn,
			createdTime: protection.createdTime,
		}
	}
	return null
}

const toFullProtection = (protection: IncomingProtectionFull): AnyFullProtection | null => {
	if (isHailProtection(protection)) {
		const hail = parseProtection(protection, HailProtectionSchema)
		if (!hail) return null
		return toForecastHailProtection(hail)
	}
	if (isWindProtection(protection)) {
		const wind = parseProtection(protection, WindProtectionSchema)
		if (!wind) return null
		return toForecastWindProtection(wind)
	}
	if (isWeatherHazardProtection(protection)) {
		const weatherHazard = parseProtection(protection, WeatherHazardProtectionSchema)
		if (!weatherHazard) return null
		return toFullWeatherHazardProtection(weatherHazard)
	}
	if (isPrecipitationProtection(protection)) {
		return toForecastPrecipitationProtection(protection)
	}
	return null
}

const parseProtection = <T extends z.ZodSchema>(protection: IncomingProtectionFull, schema: T): z.infer<T> | null => {
	const result = schema.safeParse(protection)
	if (!result.success) {
		console.error('Failed to parse protection:', result.error)
		return null
	}
	return result.data
}

const toForecastWindProtection = (wind: AppSyncWindProtection): ForecastWindProtection | null => {
	const parentRule = wind.parentRule
	if (!parentRule || !Array.isArray(parentRule.rules) || !parentRule.rules[0]) return null

	const rule = parentRule.rules[0]
	// Extract WIND_SPEED and WIND_GUST from rule.rules
	const WIND_SPEED = rule.rules?.find((r) => r.parameter === 'WIND_SPEED')?.trigger?.value
	const WIND_GUST = rule.rules?.find((r) => r.parameter === 'WIND_GUST')?.trigger?.value
	const height = typeof wind.context.heightMeters === 'number' ? wind.context.heightMeters : undefined
	const operation = rule.logicalKind === 'AND' || rule.logicalKind === 'OR' ? rule.logicalKind : undefined

	if (typeof WIND_SPEED !== 'number' && typeof WIND_GUST !== 'number') return null
	if (typeof height !== 'number') return null
	if (!operation) return null

	return {
		type: 'wind',
		parameterType: 'forecast',
		priority: ProtectionPriority.Wind,
		vrn: wind.vrn,
		assetVrn: wind.context.assetVrn,
		createdTime: wind.createdTime,
		WIND_SPEED,
		WIND_GUST,
		height,
		horizonMinutes: rule.horizonMinutes,
		operation,
		userLists: parseScheduledUserList(wind.notification),
	}
}

const toFullWeatherHazardProtection = (weatherHazard: AppSyncWeatherHazardProtection): FullWeatherHazardProtection | null => {
	const parentRule = weatherHazard.parentRule
	const capSeverity = parentRule.rules.find(
		(r): r is CategoricalRule => r.kind === 'CATEGORICAL' && r.parameter === 'WEATHER_HAZARD_SEVERITY',
	)?.trigger.value
	if (!capSeverity) return null
	return {
		type: 'weatherHazard',
		parameterType: 'forecast',
		priority: ProtectionPriority.WeatherHazard,
		vrn: weatherHazard.vrn,
		assetVrn: weatherHazard.context.assetVrn,
		createdTime: weatherHazard.createdTime,
		capSeverity: capSeverity.map((s) => parseCapSeverity(s)),
		userLists: parseImmediateUserList(weatherHazard.notification),
	}
}

// This schema is a subset of ProtectionSchema, only for short-form protections
export const IncomingProtectionSchema = ProtectionSchema.pick({
	vrn: true,
	priority: true,
	context: true,
	parentRule: true,
	createdTime: true,
}).extend({
	parentRule: z.object({
		sources: z.string().transform((sources) => RuleSourcesSchema.parse(JSON.parse(sources))),
	}),
})

export type IncomingProtection = z.infer<typeof IncomingProtectionSchema>

export const IncomingProtectionFullSchema = ProtectionSchema.extend({
	parentRule: ProtectionSchema.shape.parentRule.extend({
		sources: z.string().transform((sources) => RuleSourcesSchema.parse(JSON.parse(sources))),
		rules: z
			.string()
			.array()
			.transform((rules) => rules.map((stringRule) => ChildRuleSchema.parse(JSON.parse(stringRule)))),
	}),
})

export type IncomingProtectionFull = z.infer<typeof IncomingProtectionFullSchema>

const isHailProtection = (protection: IncomingProtectionFull | IncomingProtection): boolean =>
	'HAIL_THREAT' in protection.parentRule.sources
const isWeatherHazardProtection = (protection: IncomingProtectionFull | IncomingProtection): boolean =>
	'WEATHER_HAZARD' in protection.parentRule.sources
const isWindProtection = (protection: IncomingProtectionFull | IncomingProtection): boolean =>
	'ERG_ECMWF_HRES' in protection.parentRule.sources
const isPrecipitationProtection = (protection: IncomingProtectionFull | IncomingProtection): boolean =>
	'XWEATHER_FORECASTS' in protection.parentRule.sources
const isLtzProtection = (protection: IncomingProtection): boolean => 'LIGHTNING_THREAT' in protection.parentRule.sources
